// scoutReportBase.component.jsx
// Scout Report UI inside a BigBox (full-screen, NOT draggable)

import React, { useEffect, useState, useMemo, useRef } from "react";

// ✅ Swap DragBox -> BigBox
import BigBox from "../../../Universals/DragBox/BigBox/bigBox.component.jsx";

import { getUserFromToken } from "../../../../utils/functions.ts";
import { useDataLayer } from "../../../../Contexts/DataLayer.context.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { getScoutReports } from "../../../../Contexts/variables.jsx";
import CreateScoutReportForm from "./createReport.component.jsx";
import DigitalScouting from "./digitalScout.component.jsx";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";
import { getTodayEasternYMD } from "../../../../utils/dateUtils.ts";
import { buildPlayerWindowTitle } from "../../../../utils/playerTitle.js";
import {
    PLAYER_SCROLL_STICKY_HEADER_PX,
    PLAYER_SCROLL_TARGET_ROW,
    smoothScrollRowIntoView,
} from "../../../../utils/scrollUtils.js";

import "./scoutReportCss.scss";
import HardData from "./harddata.component.jsx";

// ✅ NEW: extracted report list
import ReportList from "./reportList.component.jsx";

const MENU = {
    SUMMARY: "SUMMARY",
    CATEGORY: "CATEGORY",
    EXPOSURE: "EXPOSURE",
    DIGITAL: "DIGITAL",
    HARD: "HARD",
};

const REPORT_TEXT_WEIGHT_STORAGE_KEY = "FBX_SCOUT_REPORT_TEXT_WEIGHT";

const readReportTextWeightPreference = () => {
    try {
        const saved = localStorage.getItem(REPORT_TEXT_WEIGHT_STORAGE_KEY);
        return saved === "regular" ? "regular" : "bold";
    } catch {
        return "bold";
    }
};

const parseWhatBoxesToMenus = (whatBoxes) => {
    if (!whatBoxes || typeof whatBoxes !== "string") {
        return [MENU.SUMMARY];
    }

    const tokens = whatBoxes
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);

    const found = new Set();

    tokens.forEach((item) => {
        if (item.includes("SUMMARY")) found.add(MENU.SUMMARY);
        if (item.includes("CATEGORY")) found.add(MENU.CATEGORY);
        if (item.includes("EXPOSPOSURE")) found.add(MENU.EXPOSURE);
        if (item.includes("EXPOSURE")) found.add(MENU.EXPOSURE);
        if (item.includes("DIGIT")) found.add(MENU.DIGITAL);
        if (item.includes("HARD")) found.add(MENU.HARD);
    });

    if (found.size === 0) found.add(MENU.SUMMARY);

    const MENU_ORDER = [
        MENU.SUMMARY,
        MENU.CATEGORY,
        MENU.EXPOSURE,
        MENU.DIGITAL,
        MENU.HARD,
    ];

    return MENU_ORDER.filter((m) => found.has(m));
};

export default function ScoutReportBase({
    // Top header values (you can pass real values later)
    playerName = "-",
    position = "-",
    school = "-",
    createdOn = "-",
    lastUpdated = "-",

    // Grade bar values
    finalGrade = "",
    calcGrade = "",
    scoutBy = "",
    reportDate = "",
    gradeCode = "",
    jersey = "",

    // Callbacks (optional)
    onClose = () => { },
    onNew = () => { },
    onTogglePrivate = () => { },
    onSaveReport = null, // optional callback: receives full editReport on save
}) {
    const API_ENDPOINT = "https://www.fbxchange.com/ReactPHP/scoutreports.php";
    const TOKEN_KEY = "scoutReportToken";

    // 🔁 one-shot refresh key for reloading reports
    const [refreshKey, setRefreshKey] = useState(0);

    const { updateActiveSideButton } = useNavBar();

    // Allow multi-select: start with SUMMARY selected
    const [selectedMenus, setSelectedMenus] = useState([MENU.SUMMARY]);
    const [theReport, setTheReport] = useState({}); // selected report (raw)
    const [editReport, setEditReport] = useState(null); // editable clone of theReport
    const [showReportForm, setShowReportForm] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [privateCount, setPrivateCount] = useState(0);
    const [whosreport, setWhosreport] = useState("NOTME");
    const [isDirty, setIsDirty] = useState(false); // track if report has unsaved changes
    const [isSaving, setIsSaving] = useState(false);
    const cancelBlurSaveRef = useRef(false);
    const savedReportRef = useRef({});
    const activeReportIdRef = useRef(null);
    const playerRowRefs = useRef({});
    const cancelPlayerScrollRef = useRef(null);
    const [currentScoutId, setCurrentScoutId] = useState(null); // 👈 who is working
    const [showGradeScale, setShowGradeScale] = useState(false);
    const [showGradeCriteria, setShowGradeCriteria] = useState(false);
    const [reportTextWeight, setReportTextWeight] = useState(readReportTextWeightPreference);

    // ✅ delete confirm box state
    const [showDeleteBox, setShowDeleteBox] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    // grab the full context object so we can safely access optional setters
    const playerCtx = usePlayer() || {};
    const {
        activePlayer,
        activeReports,
        reportsSet,
        setActiveReports,
        playerData,
        originalPlayerData,
        setSavedOriginalPlayerData,
        setActivePlayer,
        playerID,
        setPlayerID,
    } = playerCtx;

    const setPlayerDataFn =
        playerCtx.setPlayerData ||
        playerCtx.updatePlayerData ||
        playerCtx.setPlayerArray ||
        playerCtx.updatePlayerArray;

    // Close the delete confirm box when changing players
    useEffect(() => {
        setShowDeleteBox(false);
        setDeleteError("");
    }, [activePlayer?.FBXID]);

    const gradeScaleData = useMemo(() => {
        const raw =
            theReport?.gradeScale ??
            theReport?.gradeScaleArray ??
            editReport?.gradeScale ??
            editReport?.gradeScaleArray ??
            null;

        if (!raw) return [];
        if (Array.isArray(raw)) return raw;

        if (typeof raw === "string") {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                return [];
            }
        }

        return [];
    }, [theReport, editReport]);

    const gradeCriteriaData = useMemo(() => {
        const raw =
            theReport?.gradeCriteria ??
            theReport?.gradeCriteriaArray ??
            editReport?.gradeCriteria ??
            editReport?.gradeCriteriaArray ??
            null;

        if (!raw) return [];
        if (Array.isArray(raw)) return raw;

        if (typeof raw === "string") {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                return [];
            }
        }

        return [];
    }, [theReport, editReport]);

    const { reportData, updateReportData } = useDataLayer();

    const getTodayLocal = () => getTodayEasternYMD();

    // NEW REPORT (create form) state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newReport, setNewReport] = useState({
        REPORT_DATE: getTodayLocal(),
        COL_POS1: "",
        JRSEY: "",
        isPrivate: true, // true = Private, false = Not Private
    });

    const newFormButton = () => {
        setShowCreateForm(true);
    };

    const selectedSet = useMemo(() => new Set(selectedMenus), [selectedMenus]);
    const reportTextWeightClass = `scout-report-copy-${reportTextWeight}`;

    const handleReportTextWeightChange = (nextWeight) => {
        const normalized = nextWeight === "regular" ? "regular" : "bold";
        setReportTextWeight(normalized);
        try {
            localStorage.setItem(REPORT_TEXT_WEIGHT_STORAGE_KEY, normalized);
        } catch { }
    };

    // 🔒 When dirty and it's my report, lock the left column (reports + players)

    // Load current scout ID once from token
    useEffect(() => {
        (async () => {
            try {
                const user = await getUserFromToken();
                if (user && user.scout) {
                    const id = parseInt(user.scout, 10);
                    if (!Number.isNaN(id)) setCurrentScoutId(id);
                }
            } catch (err) {
                console.error("Error loading current scout id:", err);
            }
        })();
    }, []);

    // Keep newReport.COL_POS1 aligned with activePlayer position
    useEffect(() => {
        setNewReport((prev) => ({
            ...prev,
            COL_POS1: activePlayer?.colPos1 || prev.COL_POS1,
        }));
    }, [activePlayer]);

    const readReportDataMenus = () => {
        const defaultMenus = [MENU.SUMMARY];

        if (!activePlayer || !reportData || !Array.isArray(reportData)) return defaultMenus;

        const entry = reportData.find((r) => String(r.FBXID) === String(activePlayer.FBXID));
        if (!entry) return defaultMenus;

        const boxesString = entry.whatBoxes;
        if (!boxesString || typeof boxesString !== "string") return defaultMenus;

        return parseWhatBoxesToMenus(boxesString);
    };

    const menusToCsv = (menus) => {
        if (!Array.isArray(menus) || menus.length === 0) return "";
        const MENU_ORDER = [MENU.SUMMARY, MENU.CATEGORY, MENU.EXPOSURE, MENU.DIGITAL, MENU.HARD];
        const set = new Set(menus);
        return MENU_ORDER.filter((m) => set.has(m)).join(",");
    };

    const saveReadReportData = async (csvString) => {
        const reportId = theReport?.id;
        if (!reportId) return;

        const mine = whosreport;

        try {
            await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "updateReadReportData",
                    Dr_SR_Num: reportId,
                    readReportData: csvString,
                    mine: mine,
                }),
            });
        } catch (err) {
            console.error("Failed to save readReportData:", err);
        }
    };

    const toggleMenu = (menuKey) => {
        setSelectedMenus((prev) => {
            const MENU_ORDER = [MENU.SUMMARY, MENU.CATEGORY, MENU.EXPOSURE, MENU.DIGITAL, MENU.HARD];

            let nextMenus;

            if (prev.includes(menuKey)) {
                if (prev.length === 1) return prev; // keep at least one
                nextMenus = prev.filter((m) => m !== menuKey);
            } else {
                const nextSet = new Set([...prev, menuKey]);
                nextMenus = MENU_ORDER.filter((m) => nextSet.has(m));
            }

            const csv = menusToCsv(nextMenus);
            const fieldName = whosreport === "ME" ? "whatBoxes" : "readReportData";

            setTheReport((prevReport) => {
                if (!prevReport) return prevReport;

                const updated = { ...prevReport, [fieldName]: csv };

                setActiveReports((prevList) => {
                    if (!prevList) return prevList;
                    return prevList.map((r) =>
                        String(r.id) === String(updated.id) ? { ...r, [fieldName]: csv } : r
                    );
                });

                return updated;
            });

            saveReadReportData(csv);
            return nextMenus;
        });
    };

    const NavButton = ({ label, isActive, onClick }) => (
        <button
            type="button"
            className={`btn ${isActive ? "btn-primary" : "btn-outline-primary"} btn-sm text-uppercase`}
            onClick={onClick}
            style={{ minWidth: 110 }}
        >
            {label}
        </button>
    );

    // Generic field change: updates editReport only and marks dirty
    const handleFieldChange = (field, value) => {
        if (whosreport === "NOTME") return;
        setEditReport((prev) => ({
            ...(prev || {}),
            [field]: value,
        }));
        setIsDirty(true);
    };

    const buildChangedFields = (original = {}, edited = {}) => {
        const changes = {};
        if (!edited) return changes;

        Object.keys(edited).forEach((key) => {
            if (key === "id") return;
            if (edited[key] !== original[key]) changes[key] = edited[key];
        });

        return changes;
    };

    const patchMasterGradeInContext = (FBXID, nextGrade, reportID = null, reportFinal = undefined) => {
        const normalizedGrade =
            nextGrade === null || nextGrade === undefined || String(nextGrade).trim() === ""
                ? "-"
                : String(nextGrade);
        const normalizedReportFinal =
            reportFinal === null || reportFinal === undefined || String(reportFinal).trim() === ""
                ? undefined
                : String(reportFinal);

        const rowMatchesReport = (p) => {
            if (!reportID) return true;
            const rowReportID =
                p?.ScoutReport ??
                p?.Dr_SR_Num ??
                p?.id ??
                p?.reportID ??
                p?.scoutReportID ??
                null;
            return rowReportID === null || String(rowReportID) === String(reportID);
        };

        const rowMatchesPlayer = (p) => {
            const rowPlayerID =
                p?.FBXID ??
                p?.DR_Num ??
                p?.DRNUM ??
                p?.DRNUMS ??
                p?.playerID ??
                p?.id ??
                null;
            return rowPlayerID !== null && String(rowPlayerID) === String(FBXID);
        };

        const patchArray = (list) => {
            if (!Array.isArray(list)) return list;
            return list.map((p) => {
                if (!rowMatchesPlayer(p)) return p;

                const next = { ...p, masterGrade: normalizedGrade };

                if (normalizedReportFinal !== undefined && rowMatchesReport(p)) {
                    next.finalOut = normalizedReportFinal;
                    next.FINAL = normalizedReportFinal;
                    next.special_care = normalizedReportFinal;
                }

                return next;
            });
        };

        if (typeof setPlayerDataFn === "function") {
            setPlayerDataFn((prev) => patchArray(Array.isArray(prev) ? prev : playerData));
        }

        if (typeof setSavedOriginalPlayerData === "function") {
            setSavedOriginalPlayerData((prev) => patchArray(Array.isArray(prev) ? prev : originalPlayerData));
        }

        if (activePlayer && String(activePlayer?.FBXID) === String(FBXID) && typeof setActivePlayer === "function") {
            setActivePlayer((prev) => {
                const base = prev || activePlayer;
                const next = { ...base, masterGrade: normalizedGrade };
                if (normalizedReportFinal !== undefined && rowMatchesReport(base)) {
                    next.finalOut = normalizedReportFinal;
                    next.FINAL = normalizedReportFinal;
                    next.special_care = normalizedReportFinal;
                }
                return next;
            });
        }

        try {
            window.dispatchEvent(
                new CustomEvent("FBX_PLAYER_GRADE_UPDATE", {
                    detail: {
                        FBXID,
                        masterGrade: normalizedGrade,
                        reportID,
                        reportFinal: normalizedReportFinal,
                    },
                })
            );
        } catch {}
    };

    const patchPlayerRowInContext = (playerRow) => {
        if (!playerRow?.FBXID) return;

        const patchArray = (list) => {
            if (!Array.isArray(list)) return list;
            return list.map((p) =>
                String(p?.FBXID ?? p?.DR_Num ?? p?.DRNUM ?? p?.playerID ?? "") === String(playerRow.FBXID)
                    ? { ...p, ...playerRow }
                    : p
            );
        };

        if (typeof setPlayerDataFn === "function") {
            setPlayerDataFn((prev) => patchArray(Array.isArray(prev) ? prev : playerData));
        }

        if (typeof setSavedOriginalPlayerData === "function") {
            setSavedOriginalPlayerData((prev) => patchArray(Array.isArray(prev) ? prev : originalPlayerData));
        }

        if (activePlayer && String(activePlayer?.FBXID) === String(playerRow.FBXID) && typeof setActivePlayer === "function") {
            setActivePlayer((prev) => ({ ...(prev || activePlayer), ...playerRow }));
        }

        try {
            window.dispatchEvent(
                new CustomEvent("FBX_PLAYER_ROW_UPDATE", {
                    detail: { playerRow },
                })
            );
        } catch {}

    };

    const handleEditableFieldFocus = (event) => {
        if (whosreport === "NOTME") return;

        const input = event?.currentTarget;
        if (input && typeof input.setSelectionRange === "function") {
            requestAnimationFrame(() => {
                const end = String(input.value ?? "").length;
                input.setSelectionRange(end, end);
            });
        }
    };

    const cancelActiveFieldEdit = (event) => {
        event?.preventDefault?.();
        if (!isDirty) return;
        cancelBlurSaveRef.current = true;

        setEditReport({ ...(savedReportRef.current || {}) });
        setIsDirty(false);

        setTimeout(() => {
            cancelBlurSaveRef.current = false;
        }, 0);
    };

    const getEditableFieldProps = () => ({
        onFocus: (event) => handleEditableFieldFocus(event),
        onBlur: () => {
            if (cancelBlurSaveRef.current) {
                return;
            }
        },
    });

    // NEW REPORT handlers
    const handleNewReportChange = (field, value) => {
        setNewReport((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNewPrivacyToggle = (isPrivate) => {
        setNewReport((prev) => ({
            ...prev,
            isPrivate,
        }));
    };

    const handleNewToday = () => {
        const today = getTodayLocal();
        setNewReport((prev) => ({
            ...prev,
            REPORT_DATE: today,
        }));
    };

    const handleNewCancel = () => {
        setShowCreateForm(false);
        setRefreshKey((prev) => prev + 1);
    };

    const handleCreateNewReport = () => {
        if (!activePlayer) return;

        const payload = {
            FBXID: activePlayer.FBXID,
            REPORT_DATE: newReport.REPORT_DATE,
            COL_POS1: newReport.COL_POS1 || activePlayer.colPos1 || "",
            JRSEY: newReport.JRSEY,
            isPrivate: newReport.isPrivate ? "Y" : "N",
        };

        setShowCreateForm(false);
    };

    // Save handler: send only changed fields + id to backend
    const handleSaveClick = async () => {
        if (isSaving) return;
        if (!editReport || !theReport || !theReport.id) return;

        const savedReport = savedReportRef.current && Object.keys(savedReportRef.current).length > 0
            ? savedReportRef.current
            : theReport;
        const changes = buildChangedFields(savedReport, editReport);
        delete changes.digital2025;

        if (Object.keys(changes).length === 0) {
            setIsDirty(false);
            return;
        }

        const user = await getUserFromToken().catch(() => null);

        const payload = {
            scriptName: "updateScoutReportFields",
            Dr_SR_Num: theReport.id,
            updates: changes,
            scout: user?.scout ?? "",
            team: user?.team ?? "",
        };

        setIsSaving(true);
        try {
            const res = await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                console.error("Failed to save report", res.status);
                return;
            }

            const json = await res.json().catch(() => null);

            savedReportRef.current = { ...savedReport, ...changes };

            setActiveReports((prevList) => {
                if (!prevList) return prevList;
                return prevList.map((r) =>
                    String(r.id) === String(editReport.id) ? { ...r, ...changes } : r
                );
            });

            const fallbackPlayerID = json?.playerID ?? activePlayer?.FBXID ?? editReport?.FBXID ?? theReport?.FBXID;
            const currentGrade = Number(activePlayer?.masterGrade ?? 0);
            const savedFinal = Number(changes?.FINAL ?? 0);
            const fallbackMasterGrade =
                json?.masterGrade ??
                (savedFinal > currentGrade ? changes.FINAL : undefined);

            if (fallbackMasterGrade !== undefined && fallbackPlayerID) {
                patchMasterGradeInContext(
                    fallbackPlayerID,
                    fallbackMasterGrade,
                    json?.reportID ?? theReport?.id,
                    json?.reportFinal ?? changes?.FINAL
                );
            }

            if (json?.reportCount !== undefined && json?.playerID) {
                patchReportCountInContext(json.playerID, json.reportCount);
            }

            if (typeof onSaveReport === "function") onSaveReport(editReport);

            if (json?.playerRow) {
                patchPlayerRowInContext(json.playerRow);
            }

            setIsDirty(false);
        } catch (err) {
            console.error("Error saving report", err);
        } finally {
            setIsSaving(false);
        }
    };

    const canModifyCurrent = !!theReport && !!theReport.id && whosreport === "ME";

    const isPrivateFlag = theReport?.isPrivate === "Y";
    const privateButtonLabel = isPrivateFlag ? "Set Public" : "Set Private";
    const privateButtonClass = isPrivateFlag ? "btn btn-purple btn-sm" : "btn btn-outline-purple btn-sm";

    const handleCloseReports = () => {
        updateActiveSideButton(null);
        if (typeof onClose === "function") onClose();
    };

    const handlePrivateClick = async () => {
        if (!canModifyCurrent || !theReport) return;

        const isPriv =
            theReport.isPrivate === "Y" ||
            theReport.isPrivate === "1" ||
            theReport.isPrivate === 1 ||
            theReport.isPrivate === true;

        const newVal = isPriv ? "N" : "Y";

        setTheReport((prev) => (prev ? { ...prev, isPrivate: newVal } : prev));
        setEditReport((prev) => (prev ? { ...prev, isPrivate: newVal } : prev));

        setActiveReports((prevList) => {
            if (!prevList) return prevList;
            return prevList.map((r) =>
                String(r.id) === String(theReport.id) ? { ...r, isPrivate: newVal } : r
            );
        });

        setPrivateCount((prev) => {
            if (typeof prev !== "number") return prev;
            return prev + (newVal === "Y" ? 1 : -1);
        });

        if (typeof onTogglePrivate === "function") {
            onTogglePrivate({ ...theReport, isPrivate: newVal }, newVal === "Y");
        }

        try {
            await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "updateScoutReportFields",
                    Dr_SR_Num: theReport.id,
                    updates: { isPrivate: newVal },
                }),
            });
        } catch (err) {
            console.error("Failed to update isPrivate", err);
        }
    };

    const cancelDeleteUI = () => {
        if (isDeleting) return;
        setShowDeleteBox(false);
        setDeleteError("");
    };

    const handleDeleteClick = () => {
        if (!canModifyCurrent) return;
        setDeleteError("");
        setShowDeleteBox(true);
    };

    const handleDeleteCancel = () => {
        cancelDeleteUI();
    };

    // ✅ keep the RNF one-liner in sync (reports count) without requiring a full page refresh
    const patchReportCountInContext = (FBXID, newCount) => {
        const nextCountStr = String(newCount ?? 0);

        // update the player list array (this is what the left-side one-liner uses)
        if (Array.isArray(playerData) && typeof setPlayerDataFn === "function") {
            const updated = playerData.map((p) =>
                String(p?.FBXID) === String(FBXID) ? { ...p, report: nextCountStr } : p
            );
            setPlayerDataFn(updated);
        }

        // also patch activePlayer (helps any other UI bound to activePlayer)
        if (activePlayer && String(activePlayer?.FBXID) === String(FBXID) && typeof setActivePlayer === "function") {
            setActivePlayer({ ...activePlayer, report: nextCountStr });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!canModifyCurrent || !theReport?.id) return;

        setIsDeleting(true);
        setDeleteError("");

        const user = await getUserFromToken().catch(() => null);
        if (!user) {
            alert("Could not read user session. Please log in again.");
            return;
        }

        try {
            const res = await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "deleteScoutReport",
                    Dr_SR_Num: theReport.id,
                    scoutnum: user.scout,
                    team: user.team,
                    playerID: activePlayer?.FBXID,
                }),
            });

            const json = await res.json().catch(() => null);

            if (!res.ok || !json || json.success !== true) {
                const msg = json?.error || json?.message || `Delete failed (HTTP ${res.status})`;
                throw new Error(msg);
            }

            // ✅ update RNF (reports count) immediately
            patchReportCountInContext(activePlayer?.FBXID, json?.reportCount);

            if (json?.playerRow) {
                patchPlayerRowInContext(json.playerRow);
            } else if (json?.masterGrade !== undefined && json?.playerID) {
                patchMasterGradeInContext(json.playerID, json.masterGrade, json.deleteId);
            }

            // If the token points to this report, clear it
            try {
                const list = Array.isArray(reportData) ? [...reportData] : [];
                const idx = list.findIndex((r) => String(r.FBXID) === String(activePlayer?.FBXID));
                if (idx !== -1 && String(list[idx]?.ScoutReport) === String(theReport.id)) {
                    list[idx] = { ...list[idx], ScoutReport: -1 };
                    updateReportData(list);
                    localStorage.setItem(TOKEN_KEY, JSON.stringify(list));
                }
            } catch (e) {
                console.warn("Failed to update scoutReportToken after delete:", e);
            }

            setShowDeleteBox(false);
            setTheReport({});
            setEditReport(null);
            setRefreshKey((prev) => prev + 1);
        } catch (err) {
            console.error(err);
            setDeleteError(err?.message || "Delete failed.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDigitalChange = (payload) => {
        const id = payload?.reportId ?? theReport?.id;
        if (!id) return;

        const newCalc =
            payload?.overallAverage != null ? Number(payload.overallAverage).toFixed(1) : "";
        const savedReport = savedReportRef.current && Object.keys(savedReportRef.current).length > 0
            ? savedReportRef.current
            : theReport;
        const pendingReportChanges =
            whosreport === "ME" && editReport
                ? buildChangedFields(savedReport, editReport)
                : {};
        delete pendingReportChanges.digital2025;
        delete pendingReportChanges.Final_Calculated;
        const hasPendingReportChanges = Object.keys(pendingReportChanges).length > 0;

        setTheReport((prev) => {
            if (!prev) return prev;
            if (String(prev.id) !== String(id)) return prev;
            return {
                ...prev,
                ...pendingReportChanges,
                Final_Calculated: newCalc,
                digital2025: payload.digitalBlocks,
            };
        });

        setEditReport((prev) => {
            if (!prev) return prev;
            if (String(prev.id) !== String(id)) return prev;
            return {
                ...prev,
                ...pendingReportChanges,
                Final_Calculated: newCalc,
                digital2025: payload.digitalBlocks,
            };
        });

        setActiveReports((prevList) => {
            if (!prevList) return prevList;
            return prevList.map((r) =>
                String(r.id) === String(id)
                    ? { ...r, ...pendingReportChanges, Final_Calculated: newCalc, digital2025: payload.digitalBlocks }
                    : r
            );
        });

        const markDigitalSaved = () => {
            savedReportRef.current = {
                ...(savedReportRef.current || savedReport || {}),
                Final_Calculated: newCalc,
                digital2025: payload.digitalBlocks,
            };
        };

        const markPendingReportChangesSaved = () => {
            savedReportRef.current = {
                ...(savedReportRef.current || savedReport || {}),
                ...pendingReportChanges,
            };
            setIsDirty(false);
        };

        const savePayload = {
            scriptName: "updateDigitalScouting",
            Dr_SR_Num: id,
            updates: {
                Final_Calculated: newCalc,
                digital2025: payload.digitalBlocks,
            },
        };

        try {
            fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(savePayload),
            })
                .then((res) => {
                    if (!res.ok) throw new Error(`Digital autosave failed (${res.status})`);
                    markDigitalSaved();
                    return res;
                })
                .catch((err) => console.error("Digital autosave failed", err));
        } catch (err) {
            console.error("Digital autosave scheduling error", err);
        }

        if (hasPendingReportChanges) {
            getUserFromToken()
                .catch(() => null)
                .then((user) =>
                    fetch(API_ENDPOINT, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            scriptName: "updateScoutReportFields",
                            Dr_SR_Num: id,
                            updates: pendingReportChanges,
                            scout: user?.scout ?? "",
                            team: user?.team ?? "",
                        }),
                    })
                )
                .then((res) => {
                    if (!res.ok) throw new Error(`Pending report save failed (${res.status})`);
                    markPendingReportChangesSaved();
                    return res.json().catch(() => null);
                })
                .catch((err) => console.error("Pending report autosave failed", err));
        }
    };

    const handleAiSummary = async (sections, mode = "merge") => {
        if (whosreport === "NOTME") {
            throw new Error("You can only update your own report.");
        }
        if (!theReport?.id) {
            throw new Error("No active report selected.");
        }

        const clean = (value) => String(value || "").trim();
        const positive = clean(sections?.positive);
        const negative = clean(sections?.negative);
        const summary = clean(sections?.summary);

        if (!positive && !negative && !summary) {
            throw new Error("AI response is empty.");
        }

        const currentReport = editReport || theReport || {};
        const mergeValue = (currentValue, nextValue) => {
            if (!nextValue) return String(currentValue ?? "");
            if (mode === "replace") return nextValue;
            return [String(currentValue ?? "").trim(), nextValue].filter(Boolean).join("\n\n");
        };

        const updates = {};
        if (positive) updates.M_STRENGTH = mergeValue(currentReport.M_STRENGTH, positive);
        if (negative) updates.M_WEAKNESS = mergeValue(currentReport.M_WEAKNESS, negative);
        if (summary) updates.SUMMARY = mergeValue(currentReport.SUMMARY, summary);

        setEditReport((prev) => ({
            ...(prev || {}),
            ...updates,
        }));

        setTheReport((prev) => ({
            ...(prev || {}),
            ...updates,
        }));

        setActiveReports((prevList) => {
            if (!prevList) return prevList;
            return prevList.map((r) =>
                String(r.id) === String(theReport.id) ? { ...r, ...updates } : r
            );
        });

        setSelectedMenus((prev) => {
            const current = Array.isArray(prev) ? prev : [];
            const next = [...current];
            if (!next.includes(MENU.SUMMARY)) next.unshift(MENU.SUMMARY);
            if ((positive || negative) && !next.includes(MENU.CATEGORY)) next.push(MENU.CATEGORY);
            return next;
        });

        const res = await fetch(API_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                scriptName: "updateScoutReportFields",
                Dr_SR_Num: theReport.id,
                updates,
            }),
        });

        if (!res.ok) {
            throw new Error(`Summary save failed (${res.status}).`);
        }
    };

    useEffect(() => {
        setActiveReports(null);

        (async () => {
            if (!reportData || reportData.length === 0) {
                const reportToken = localStorage.getItem(TOKEN_KEY);
                if (reportToken) {
                    try {
                        const parsed = JSON.parse(reportToken);
                        const reports = Array.isArray(parsed) ? parsed : [parsed];
                        updateReportData(reports);
                    } catch (e) {
                        console.warn("Failed to parse scoutReportToken:", e);
                    }
                } else if (activePlayer?.FBXID) {
                    const defaultData = [
                        {
                            FBXID: activePlayer.FBXID ?? 158243,
                            ScoutReport: -1,
                            ReportDate: "1900-01-01",
                        },
                    ];
                    localStorage.setItem(TOKEN_KEY, JSON.stringify(defaultData));
                    updateReportData(defaultData);
                }
            }
        })();

        async function loadScoutReport() {
            try {
                if (!activePlayer?.FBXID) return;
                let user = await getUserFromToken();
                let reports = await getScoutReports(activePlayer.FBXID, user);
                if (!reports.data.reports || reports.data.reports.length === 0) setShowReportForm(false);
                setTotalCount(reports.data.totalCount || 0);
                setPrivateCount(reports.data.privateCount || 0);
                setActiveReports(reports.data.reports || []);
            } catch (err) {
                if (err.name !== "AbortError") console.error("Error loading scout report:", err);
            }
        }

        if (activePlayer?.FBXID) loadScoutReport();
    }, [activePlayer?.FBXID, setActiveReports, refreshKey]);

    useEffect(() => {
        const reportId = theReport?.id ?? null;

        if (theReport && Object.keys(theReport).length > 0 && String(activeReportIdRef.current) !== String(reportId)) {
            activeReportIdRef.current = reportId;
            savedReportRef.current = theReport;
            setEditReport(theReport);
            setIsDirty(false);
        } else if (!theReport || Object.keys(theReport).length === 0) {
            activeReportIdRef.current = null;
            savedReportRef.current = {};
            setEditReport(null);
            setIsDirty(false);
        }
    }, [theReport]);

    useEffect(() => {
        if (!theReport || Object.keys(theReport).length === 0) {
            setShowReportForm(false);
            setWhosreport("NOTME");
            setSelectedMenus([MENU.SUMMARY]);
            return;
        }

        setShowReportForm(true);

        (async () => {
            try {
                const user = await getUserFromToken();
                const userId = parseInt(user.scout, 10);
                const reportScoutNum = parseInt(theReport.scoutnum, 10);

                if (userId === reportScoutNum) {
                    setWhosreport("ME");
                    const menus = parseWhatBoxesToMenus(theReport.whatBoxes);
                    setSelectedMenus(menus);
                } else {
                    setWhosreport("NOTME");
                    const menus = parseWhatBoxesToMenus(theReport.readReportData);
                    setSelectedMenus(menus);
                }
                setIsDirty(false);
            } catch (err) {
                console.error("Error determining whose report:", err);
                setWhosreport("NOTME");
                setSelectedMenus(readReportDataMenus());
                setIsDirty(false);
            }
        })();
    }, [theReport, activePlayer?.FBXID, reportData]);

    useEffect(() => {
        if (!theReport || Object.keys(theReport).length === 0) {
            setShowGradeScale(false);
            setShowGradeCriteria(false);
        }
    }, [theReport]);

    const handlePlayerClick = (player) => {
        if (!player || isDirty) return;
        if (typeof setActivePlayer === "function") {
            setActivePlayer(player);
            setPlayerID(player.FBXID);
        } else {
            console.warn("setActivePlayer is not available from usePlayer context");
        }
    };

    const letGoToNotes = () => {
        updateActiveSideButton("notes");
    };

    const letGoToFiles = () => {
        updateActiveSideButton("Files");
    };

    const hasReports = Array.isArray(activeReports) && activeReports.length > 0;
    const hasSelectedReport = theReport && Object.keys(theReport).length > 0;
    const reportsTitle = buildPlayerWindowTitle(activePlayer, "Reports");
    const currentFBXID = activePlayer?.FBXID || playerID || null;

    useEffect(() => {
        if (!currentFBXID) return;

        const frameId = requestAnimationFrame(() => {
            const row = playerRowRefs.current[String(currentFBXID)];
            cancelPlayerScrollRef.current?.();
            cancelPlayerScrollRef.current = smoothScrollRowIntoView(row, {
                targetRow: PLAYER_SCROLL_TARGET_ROW,
                stickyHeaderHeight: PLAYER_SCROLL_STICKY_HEADER_PX,
            });
        });

        return () => {
            cancelAnimationFrame(frameId);
            cancelPlayerScrollRef.current?.();
        };
    }, [currentFBXID, playerData]);

    // ✅ called by ReportList when selection changes (auto or click)
    const handleReportChosen = (r) => {
        if (isDirty) return;
        cancelDeleteUI();
        if (!r) {
            setTheReport({});
            return;
        }
        setTheReport(r);
    };

    const leftSideMask = isDirty ? (
        <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
                zIndex: 50,
                background: "rgba(255,255,255,0.45)",
                cursor: "not-allowed",
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        />
    ) : null;

    return (
        <BigBox
            storageKey="SCOUT_REPORT_BOX_POSITION"
            defaultPos={{ x: 60, y: 100 }}
            title={reportsTitle}
            width={1820}
            zIndex={2050}
            onAddText="New Report"
            addNote="Create New Report"
            onAddFunction={newFormButton}
            closeButton={true}
            onCloseButton={handleCloseReports}
            showThirdButton={true}
            thirdButtonText="View Notes"
            thirdButtonClick={() => letGoToNotes()}
            showFourthButton={true}
            fourthButtonText="View Files"
            fourthButtonClick={() => letGoToFiles()}
            backgroundColor="#f5edc7ff"
            showListDropdown={true}
            mask={isDirty}
        >
            {/* ✅ Critical: make the base fill the BigBox body height */}
            <div className="h-100" style={{ minHeight: 0 }}>
                {!showCreateForm ? (
                    <div className="d-flex gap-3 h-100" style={{ minHeight: 0 }}>
                        {/* LEFT SIDE: Reports (top) + Player list (bottom) */}
                        <div className="col-14 d-flex flex-column position-relative h-100" style={{ minHeight: 0 }}>
                            {/* TOP: REPORTS (fixed-ish height) */}
                            <div className="border rounded d-flex flex-column position-relative" style={{ flex: "0 0 305px", minHeight: 0 }}>
                                {leftSideMask}
                                <ReportList
                                    height={305}
                                    activePlayer={activePlayer}
                                    activeReports={activeReports}
                                    totalCount={totalCount}
                                    privateCount={privateCount}
                                    currentScoutId={currentScoutId}
                                    reportData={reportData}
                                    updateReportData={updateReportData}
                                    selectedReportId={theReport?.id}
                                    onReportChosen={handleReportChosen}
                                />
                            </div>

                            {/* BOTTOM: PLAYER DATA (fills remaining height) */}
                            <div className="mt-2 flex-grow-1 border rounded position-relative" style={{ minHeight: 0, overflowY: "auto" }}>
                                {leftSideMask}
                                {!playerData ? (
                                    <div className="p-2 fs-7">Loading player data...</div>
                                ) : playerData.length === 0 ? (
                                    <div className="p-2 fs-7">No players in dataset</div>
                                ) : (
                                    <div className="w-100 fs-7">
                                        <div
                                            className="d-flex gap-2 bg-navy fs-7"
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2,
                                                background: "var(--navy, #001f3f)",
                                                height: "35px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <div className="flex-grow-1 text-lemon ps-2">Name</div>
                                            <div className="text-center col-5 text-lemon">School</div>
                                            <div className="text-center col-5 text-lemon">CGP</div>
                                            <div className="text-center col-5 text-lemon">Col#</div>
                                            <div className="text-center col-5 text-lemon">Pro#</div>
                                            <div className="text-center col-5 text-lemon">RNF</div>
                                        </div>

                                        {playerData.map((p, i) => (
                                            <div
                                                key={p.FBXID ?? i}
                                                ref={(el) => {
                                                    const key = String(p.FBXID ?? "");
                                                    if (!key) return;
                                                    if (el) playerRowRefs.current[key] = el;
                                                    else delete playerRowRefs.current[key];
                                                }}
                                                className={`d-flex fs-9 gap-2 py-1 align-items-center ${String(p.FBXID) === String(currentFBXID) ? "alert-danger text-dark" : ""}`}
                                                style={{
                                                    borderBottom: "1px solid #ccc",
                                                    height: "35px",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handlePlayerClick(p)}
                                            >
                                                <div
                                                    className={`flex-grow-1 text-truncate ps-2
                            ${p.impnip === "G" ? "globalPlayer" : p.impnip?.startsWith("V") ? "brownPlayer" : ""}
                            ${p.impnip === "N" ? "text-danger" : ""}`}
                                                >
                                                    {`${(p.last || "").toUpperCase()}, ${(p.first || "").toUpperCase()}`}
                                                </div>
                                                <div className="col-5 text-center">{p.school ?? "-"}</div>
                                                <div className="col-5 text-center">{p.genPos ?? "-"}</div>
                                                <div className="col-5 text-center">{p.jersey === "-1" ? "0" : p.jersey ?? "-"}</div>
                                                <div className="col-5 text-center">{p.proJersey ?? "-"}</div>
                                                <div className="col-5 text-center">
                                                    {parseInt(p.report) === 0 ? "-" : p.report}{" "}
                                                    {parseInt(p.note) === 0 ? "-" : p.note ?? "-"}{" "}
                                                    {parseInt(p.pdf) === 0 ? "-" : p.pdf ?? "-"}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* RIGHT SIDE (fills remaining width + height) */}
                        <div className="w-100 h-100" style={{ minHeight: 0 }}>
                            <div className="container-fluid p-0 position-relative h-100" style={{ minHeight: 0 }}>
                                {/* ✅ DELETE CONFIRM OVERLAY */}
                                {showDeleteBox && (
                                    <div
                                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                        style={{
                                            zIndex: 5000,
                                            background: "rgba(0,0,0,0.35)",
                                            padding: 12,
                                        }}
                                    >
                                        <div className="alert alert-danger shadow-lg m-0" style={{ width: 700, maxWidth: "95%" }}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="fw-bold text-uppercase">Remove Scout Report</div>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    aria-label="Close"
                                                    onClick={handleDeleteCancel}
                                                    disabled={isDeleting}
                                                />
                                            </div>

                                            <div className="mt-2">
                                                <div className="fw-bold">Are you sure you want to remove this report?</div>

                                                <div className="small mt-2">
                                                    <span className="fw-bold">Report ID:</span> {theReport?.id ?? "-"}{" "}
                                                    <span className="mx-2">|</span>
                                                    <span className="fw-bold">Date:</span> {theReport?.REPORT_DATE ?? "-"}{" "}
                                                    <span className="mx-2">|</span>
                                                    <span className="fw-bold">Exposure:</span> {theReport?.exposure ?? "-"}
                                                </div>

                                                {deleteError ? (
                                                    <div className="alert alert-warning mt-3 mb-0 py-2">{deleteError}</div>
                                                ) : null}
                                            </div>

                                            <div className="d-flex gap-2 justify-content-end mt-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={handleDeleteCancel}
                                                    disabled={isDeleting}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-danger"
                                                    onClick={handleDeleteConfirm}
                                                    disabled={isDeleting}
                                                >
                                                    {isDeleting ? "Removing..." : "Remove Report"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {hasReports && hasSelectedReport ? (
                                    <div className="row justify-content-center h-100" style={{ minHeight: 0 }}>
                                        {/* ✅ Make the whole right panel a flex column so the scroll area can fill */}
                                        <div className="col-48 bg-white rounded shadow-sm p-0 d-flex flex-column h-100" style={{ minHeight: 0 }}>
                                            {/* === Top meta / grade card === */}
                                            <div id="topBlueBar" className={`card border-0 shadow-sm mb-0 ${reportTextWeightClass}`}>
                                                <div className="w-100 px-2 py-2 border border-rounded border-1 border-navy">
                                                    <div className="row g-2 align-items-end">
                                                        <div className="col-4" id="grade_box-up" style={{ zIndex: 1000 }}>
                                                            <label htmlFor="FINAL" className="form-label mb-1 small text-muted">
                                                                Grade
                                                            </label>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    id="FINAL"
                                                                    className="form-control scout-report-copy-field"
                                                                    value={editReport?.FINAL ?? ""}
                                                                    onChange={(e) => handleFieldChange("FINAL", e.target.value)}
                                                                    {...getEditableFieldProps("FINAL")}
                                                                    readOnly={whosreport === "NOTME"}
                                                                    placeholder="Final"
                                                                />
                                                                <button
                                                                    className="btn btn-outline-warning"
                                                                    type="button"
                                                                    id="criteriaButton2"
                                                                    title="Grade criteria"
                                                                    onClick={() => setShowGradeScale((v) => !v)}
                                                                >
                                                                    ?
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="col-4 d-none d-md-block">
                                                            <label htmlFor="Final_Calculated" className="form-label mb-1 small text-muted">
                                                                Calc. Grade
                                                            </label>
                                                            <input
                                                                id="Final_Calculated"
                                                                className="form-control form-control-sm scout-report-copy-field"
                                                                value={editReport?.Final_Calculated ?? ""}
                                                                readOnly
                                                                placeholder="Calc."
                                                            />
                                                        </div>

                                                        <div className="col-4" style={{ zIndex: 1000 }}>
                                                            <label htmlFor="gradeCode" className="form-label mb-1 small text-muted">
                                                                Codes
                                                            </label>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    id="gradeCode"
                                                                    className="form-control scout-report-copy-field"
                                                                    value={editReport?.gradeCode ?? "--"}
                                                                    onChange={(e) => handleFieldChange("gradeCode", e.target.value)}
                                                                    {...getEditableFieldProps("gradeCode")}
                                                                    readOnly={whosreport === "NOTME"}
                                                                    placeholder="Code"
                                                                />
                                                                <button
                                                                    className="btn btn-outline-warning"
                                                                    type="button"
                                                                    id="criteriaButton3"
                                                                    title="Code help"
                                                                    onClick={() => setShowGradeCriteria((v) => !v)}
                                                                >
                                                                    ?
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="col-4 d-none d-lg-block">
                                                            <label htmlFor="sr_by" className="form-label mb-1 small text-muted">
                                                                Report Pos
                                                            </label>
                                                            <input
                                                                id="sr_by"
                                                                className="form-control form-control-sm scout-report-copy-field"
                                                                value={editReport?.COL_POS1 ?? ""}
                                                                readOnly
                                                                placeholder="Report Pos"
                                                            />
                                                        </div>

                                                        <div className="col-7 d-none d-lg-block">
                                                            <label htmlFor="sr_by" className="form-label mb-1 small text-muted">
                                                                Scout
                                                            </label>
                                                            <input
                                                                id="sr_by"
                                                                className="form-control form-control-sm scout-report-copy-field"
                                                                value={editReport?.SCOUT ?? ""}
                                                                readOnly
                                                                placeholder="Scout"
                                                            />
                                                        </div>

                                                        <div className="col-5">
                                                            <label htmlFor="sr_date" className="form-label mb-1 small text-muted">
                                                                Report Date
                                                            </label>
                                                            <input
                                                                id="sr_date"
                                                                className="form-control form-control-sm scout-report-copy-field"
                                                                value={editReport?.REPORT_DATE ?? ""}
                                                                readOnly
                                                                placeholder="YYYY-MM-DD"
                                                            />
                                                        </div>

                                                        <div className="col-4 d-none d-md-block">
                                                            <label htmlFor="JRSEY" className="form-label mb-1 small text-muted">
                                                                Jersey #
                                                            </label>
                                                            <input
                                                                id="JRSEY"
                                                                className="form-control form-control-sm scout-report-copy-field"
                                                                value={editReport?.JRSEY ?? ""}
                                                                onChange={(e) => handleFieldChange("JRSEY", e.target.value)}
                                                                {...getEditableFieldProps("JRSEY")}
                                                                readOnly={whosreport === "NOTME"}
                                                                placeholder="JR #"
                                                            />
                                                        </div>

                                                        <div className="col-6 d-none d-md-block">
                                                            <label className="form-label mb-1 small text-muted">
                                                                Font
                                                            </label>
                                                            <div className="d-flex gap-1 w-100" role="group" aria-label="Report font weight">
                                                                <button
                                                                    type="button"
                                                                    className={`btn ${reportTextWeight === "bold" ? "btn-primary" : "btn-outline-primary"}`}
                                                                    onClick={() => handleReportTextWeightChange("bold")}
                                                                >
                                                                    Bold
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`btn ${reportTextWeight === "regular" ? "btn-primary" : "btn-outline-primary"}`}
                                                                    onClick={() => handleReportTextWeightChange("regular")}
                                                                >
                                                                    Regular
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* === NAV ROW === */}
                                            <div className="border-top border-light px-3 py-2">
                                                <div className="d-flex align-items-center flex-wrap gap-2 w-100">
                                                    <div className="d-flex flex-wrap gap-2">
                                                        <NavButton
                                                            label="Summary"
                                                            isActive={selectedSet.has(MENU.SUMMARY)}
                                                            onClick={() => toggleMenu(MENU.SUMMARY)}
                                                        />
                                                        <NavButton
                                                            label="Category"
                                                            isActive={selectedSet.has(MENU.CATEGORY)}
                                                            onClick={() => toggleMenu(MENU.CATEGORY)}
                                                        />
                                                        <NavButton
                                                            label="Exposure"
                                                            isActive={selectedSet.has(MENU.EXPOSURE)}
                                                            onClick={() => toggleMenu(MENU.EXPOSURE)}
                                                        />
                                                        <NavButton
                                                            label="Digital"
                                                            isActive={selectedSet.has(MENU.DIGITAL)}
                                                            onClick={() => toggleMenu(MENU.DIGITAL)}
                                                        />
                                                        <NavButton
                                                            label="Hard Data"
                                                            isActive={selectedSet.has(MENU.HARD)}
                                                            onClick={() => toggleMenu(MENU.HARD)}
                                                        />
                                                    </div>

                                                    <div className="d-flex flex-wrap gap-2 ms-auto">
                                                        {whosreport !== "NOTME" && !isDirty && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className={privateButtonClass}
                                                                    disabled={!canModifyCurrent}
                                                                    onClick={handlePrivateClick}
                                                                >
                                                                    {privateButtonLabel}
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    disabled={!canModifyCurrent}
                                                                    onClick={handleDeleteClick}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </>
                                                        )}


                                                        {whosreport !== "NOTME" && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-sm ${isDirty ? "btn-secondary" : "btn-outline-secondary"}`}
                                                                    disabled={!editReport || !isDirty || isSaving}
                                                                    onMouseDown={cancelActiveFieldEdit}
                                                                    onClick={(e) => e.preventDefault()}
                                                                >
                                                                    Cancel
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-sm ${isDirty ? "btn-success" : "btn-outline-success"}`}
                                                                    disabled={!editReport || !isDirty || isSaving}
                                                                    onClick={handleSaveClick}
                                                                >
                                                                    {isSaving ? "Saving..." : "Save Report"}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* === SCROLLABLE CONTENT (fills remaining right-side height) === */}
                                            <div className="px-2 pb-3 d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
                                                <div
                                                    id="scout_report_scroll_area"
                                                    className={`mt-2 border w-100 border-rounded d-flex flex-column ${reportTextWeightClass}`}
                                                    style={{
                                                        flex: 1,
                                                        minHeight: 0,
                                                        overflowY: "auto",
                                                        overflowX: "hidden",
                                                    }}
                                                >
                                                    <div className="row g-2 p-2">
                                                        {/* HARD DATA */}
                                                        {selectedSet.has(MENU.HARD) && <HardData activePlayer={activePlayer} />}

                                                        {/* EXPOSURE */}
                                                        {selectedSet.has(MENU.EXPOSURE) && (
                                                            <div id="EXPOSURE_BOX" className="row g-2 mb-2">
                                                                <div className="col-48 d-flex justify-content-between">
                                                                    <div className="text-uppercase">Exposure</div>
                                                                    <div className="text-uppercase">Speed</div>
                                                                </div>
                                                                <div className="col-32">
                                                                    <input
                                                                        id="exposure_notes"
                                                                        className="form-control scout-report-copy-field"
                                                                        value={editReport?.exposure ?? ""}
                                                                        onChange={(e) => handleFieldChange("exposure", e.target.value)}
                                                                        {...getEditableFieldProps("exposure")}
                                                                        readOnly={whosreport === "NOTME"}
                                                                        placeholder="Exposure notes"
                                                                    />
                                                                </div>
                                                                <div className="col-16">
                                                                    <input
                                                                        id="PL_SPEED_COMMENT"
                                                                        className="form-control scout-report-copy-field"
                                                                        value={editReport?.PL_SPEED_COMMENT ?? ""}
                                                                        onChange={(e) => handleFieldChange("PL_SPEED_COMMENT", e.target.value)}
                                                                        {...getEditableFieldProps("PL_SPEED_COMMENT")}
                                                                        readOnly={whosreport === "NOTME"}
                                                                        placeholder="Speed comment"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* SUMMARY */}
                                                        {selectedSet.has(MENU.SUMMARY) && (
                                                            <div id="SUMMARY_2BOX" className="row g-2 mb-2">
                                                                <div className="col-48">
                                                                    <div id="scout_data" className="mb-2">
                                                                        <div className="text-uppercase" style={{ lineHeight: "20px" }}>
                                                                            Summary
                                                                        </div>
                                                                    </div>
                                                                    <textarea
                                                                        id="SUMMARY"
                                                                        rows={5}
                                                                        className="form-control scout-report-copy-field"
                                                                        value={editReport?.SUMMARY ?? ""}
                                                                        onChange={(e) => handleFieldChange("SUMMARY", e.target.value)}
                                                                        {...getEditableFieldProps("SUMMARY")}
                                                                        readOnly={whosreport === "NOTME"}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* CATEGORY */}
                                                        {selectedSet.has(MENU.CATEGORY) && (
                                                            <div id="THREE_BOX">
                                                                <div className="d-flex gap-2 mb-2">
                                                                    <div className="col-16">
                                                                        <label htmlFor="">Positive</label>
                                                                        <textarea
                                                                            id="positive_input"
                                                                            rows={3}
                                                                            className="form-control scout-report-copy-field"
                                                                            value={editReport?.M_STRENGTH ?? ""}
                                                                            onChange={(e) => handleFieldChange("M_STRENGTH", e.target.value)}
                                                                            {...getEditableFieldProps("M_STRENGTH")}
                                                                            readOnly={whosreport === "NOTME"}
                                                                        />
                                                                    </div>

                                                                    <div className="col-16">
                                                                        <label htmlFor="">Negative</label>
                                                                        <textarea
                                                                            id="negative_input"
                                                                            rows={3}
                                                                            className="form-control scout-report-copy-field"
                                                                            value={editReport?.M_WEAKNESS ?? ""}
                                                                            onChange={(e) => handleFieldChange("M_WEAKNESS", e.target.value)}
                                                                            {...getEditableFieldProps("M_WEAKNESS")}
                                                                            readOnly={whosreport === "NOTME"}
                                                                        />
                                                                    </div>

                                                                    <div className="flex-grow-1">
                                                                        <label htmlFor="">Character/Mental</label>
                                                                        <textarea
                                                                            id="character_input"
                                                                            rows={3}
                                                                            className="form-control scout-report-copy-field"
                                                                            value={editReport?.CHARIT ?? ""}
                                                                            onChange={(e) => handleFieldChange("CHARIT", e.target.value)}
                                                                            {...getEditableFieldProps("CHARIT")}
                                                                            readOnly={whosreport === "NOTME"}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="d-flex gap-2 mb-2">
                                                                    <div className="col-16">
                                                                        <label htmlFor="">Production</label>
                                                                        <textarea
                                                                            id="production_input"
                                                                            rows={3}
                                                                            className="form-control scout-report-copy-field"
                                                                            value={editReport?.PRODUCTION ?? ""}
                                                                            onChange={(e) => handleFieldChange("PRODUCTION", e.target.value)}
                                                                            {...getEditableFieldProps("PRODUCTION")}
                                                                            readOnly={whosreport === "NOTME"}
                                                                        />
                                                                    </div>

                                                                    <div className="col-16">
                                                                        <label htmlFor="">Medical</label>
                                                                        <textarea
                                                                            id="medical_input"
                                                                            rows={3}
                                                                            className="form-control scout-report-copy-field"
                                                                            value={editReport?.COMPETITOR ?? ""}
                                                                            onChange={(e) => handleFieldChange("COMPETITOR", e.target.value)}
                                                                            {...getEditableFieldProps("COMPETITOR")}
                                                                            readOnly={whosreport === "NOTME"}
                                                                        />
                                                                    </div>

                                                                    <div className="flex-grow-1">
                                                                        <label htmlFor="">Environment</label>
                                                                        <textarea
                                                                            id="strength_input"
                                                                            rows={3}
                                                                            className="form-control scout-report-copy-field"
                                                                            value={editReport?.STRENGTH ?? ""}
                                                                            onChange={(e) => handleFieldChange("STRENGTH", e.target.value)}
                                                                            {...getEditableFieldProps("STRENGTH")}
                                                                            readOnly={whosreport === "NOTME"}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* DIGITAL */}
                                                        {selectedSet.has(MENU.DIGITAL) && (
                                                            <DigitalScouting
                                                                reportId={theReport?.id}
                                                                reportData={theReport?.digital2025}
                                                                reportPosition={editReport?.COL_POS1 || theReport?.COL_POS1 || position}
                                                                canEdit={whosreport === "ME"}
                                                                onDigitalChange={handleDigitalChange}
                                                                onAiSummary={whosreport === "ME" ? handleAiSummary : null}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="row justify-content-center h-100" style={{ minHeight: 0 }}>
                                        <div className="col-48 h-100" style={{ minHeight: 0 }}>
                                            <div
                                                className="d-flex flex-column align-items-center justify-content-center bg-light text-muted border rounded shadow-sm h-100"
                                                style={{ minHeight: 0 }}
                                            >
                                                <div className="mb-3">
                                                    {activePlayer
                                                        ? `No Reports for ${activePlayer.last}, ${activePlayer.first}`
                                                        : "No Reports for selected player"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <CreateScoutReportForm
                        activePlayer={activePlayer}
                        newReport={newReport}
                        onChange={handleNewReportChange}
                        onTogglePrivacy={handleNewPrivacyToggle}
                        onToday={handleNewToday}
                        dateIn={newReport.REPORT_DATE ? new Date(newReport.REPORT_DATE) : null}
                        onCancel={handleNewCancel}
                    />
                )}

                {/* Grade Scale overlay */}
                {showGradeScale && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100"
                        style={{ zIndex: 6000, background: "rgba(0,0,0,0.55)" }}
                        onClick={() => setShowGradeScale(false)}
                    >
                        <div
                            className="bg-white border rounded shadow p-3"
                            style={{
                                width: "95%",
                                maxWidth: "1100px",
                                maxHeight: "85vh",
                                overflowY: "auto",
                                margin: "5vh auto",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="fw-bold">Grade Scale</div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setShowGradeScale(false)}
                                >
                                    Close
                                </button>
                            </div>

                            {gradeScaleData.length === 0 ? (
                                <div className="text-muted small">
                                    <em>No grade scale found on this report.</em>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-striped align-middle mb-0 fs-7">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "90px" }}>Final</th>
                                                <th style={{ width: "110px" }}>Range</th>
                                                <th>Final Description</th>
                                                <th>Criteria</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {gradeScaleData
                                                .slice()
                                                .sort(
                                                    (a, b) =>
                                                        Number(a.Sortorder ?? a.sortorder ?? 0) -
                                                        Number(b.Sortorder ?? b.sortorder ?? 0)
                                                )
                                                .map((g, idx) => (
                                                    <tr key={g.ID ?? idx}>
                                                        <td className="fw-bold">{g.Final ?? "-"}</td>
                                                        <td>{g.Range ?? "-"}</td>
                                                        <td>{g.Final_Description ?? "-"}</td>
                                                        <td>{g.Criteria_Description ?? "-"}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Grade Criteria overlay */}
                {showGradeCriteria && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100"
                        style={{ zIndex: 6100, background: "rgba(0,0,0,0.55)" }}
                        onClick={() => setShowGradeCriteria(false)}
                    >
                        <div
                            className="bg-white border rounded shadow p-3"
                            style={{
                                width: "95%",
                                maxWidth: "1100px",
                                maxHeight: "85vh",
                                overflowY: "auto",
                                margin: "5vh auto",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="fw-bold">Codes</div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setShowGradeCriteria(false)}
                                >
                                    Close
                                </button>
                            </div>

                            {gradeCriteriaData.length === 0 ? (
                                <div className="text-muted small">
                                    <em>No code criteria found on this report.</em>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-striped align-middle mb-0 fs-7">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "140px" }}>Code</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {gradeCriteriaData
                                                .slice()
                                                .sort(
                                                    (a, b) =>
                                                        Number(a.Sortorder ?? a.sortorder ?? 0) -
                                                        Number(b.Sortorder ?? b.sortorder ?? 0)
                                                )
                                                .map((g, idx) => (
                                                    <tr key={g.ID ?? idx}>
                                                        <td className="fw-bold">{g.Range ?? "-"}</td>
                                                        <td>{g.Final_Description ?? "-"}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </BigBox>
    );
}
